<template>
    <div>
        <card-header title="Tasks" icon="fa-tasks"/>

        <card-toolbar>
            <button @click="$openCard('task-new', {}, card)"><i class="far fa-plus"></i>New Task</button>

            <template v-slot:right>
                <b-tooltip label="My Tasks">
                    <button class="icon-only" :class="{'is-active': filter === 'my'}" @click="toggleFilter('my')"><i class="far fa-user"></i></button>
                </b-tooltip>
                <b-tooltip label="All Tasks">
                    <button class="icon-only" :class="{'is-active': filter === 'all'}" @click="toggleFilter('all')"><i class="far fa-users"></i></button>
                </b-tooltip>
                <b-tooltip label="Completed">
                    <button class="icon-only" :class="{'is-active': showCompleted}" @click="showCompleted = !showCompleted; loadTasks(true)"><i class="far fa-check"></i></button>
                </b-tooltip>
            </template>
        </card-toolbar>

        <card-body toolbar>
            <card-list>
                <subheader-list-item title="Pending" icon="fa-clock" :badge="pendingTasks.length" v-if="pendingTasks.length"/>
                <card-list-item v-for="task in pendingTasks" :key="task.id" @click="toggleTaskStatus(task)">
                    <template v-slot:icon><i class="far fa-circle fa-fw text-warning"></i></template>
                    <template v-slot:content>
                        <div class="tile-title">{{task.title}}</div>
                        <div class="meta" v-if="task.ticket_subject"><i class="fas fa-ticket fa-fw mr-1"></i>{{task.ticket_subject}}</div>
                        <div class="meta" v-if="task.assigned_first_name"><i class="fas fa-user fa-fw mr-1"></i>{{task.assigned_first_name}} {{task.assigned_last_name}}</div>
                        <div class="meta" v-if="task.due_date"><i class="fas fa-calendar fa-fw mr-1"></i>{{task.due_date | moment('MMM Do, YYYY')}}</div>
                    </template>
                    <template v-slot:status>
                        <b-tooltip label="Start">
                            <button class="icon-only is-small" @click.stop="updateTaskStatus(task, 'in_progress')"><i class="far fa-play"></i></button>
                        </b-tooltip>
                        <b-tooltip label="Complete">
                            <button class="icon-only is-small" @click.stop="updateTaskStatus(task, 'completed')"><i class="far fa-check"></i></button>
                        </b-tooltip>
                    </template>
                </card-list-item>

                <subheader-list-item title="In Progress" icon="fa-spinner" :badge="inProgressTasks.length" v-if="inProgressTasks.length"/>
                <card-list-item v-for="task in inProgressTasks" :key="task.id" @click="toggleTaskStatus(task)">
                    <template v-slot:icon><i class="fas fa-spinner fa-fw text-primary"></i></template>
                    <template v-slot:content>
                        <div class="tile-title">{{task.title}}</div>
                        <div class="meta" v-if="task.ticket_subject"><i class="fas fa-ticket fa-fw mr-1"></i>{{task.ticket_subject}}</div>
                        <div class="meta" v-if="task.assigned_first_name"><i class="fas fa-user fa-fw mr-1"></i>{{task.assigned_first_name}} {{task.assigned_last_name}}</div>
                    </template>
                    <template v-slot:status>
                        <b-tooltip label="Complete">
                            <button class="icon-only is-small" @click.stop="updateTaskStatus(task, 'completed')"><i class="far fa-check"></i></button>
                        </b-tooltip>
                    </template>
                </card-list-item>

                <subheader-list-item title="Completed" icon="fa-check" :badge="completedTasks.length" v-if="showCompleted && completedTasks.length"/>
                <card-list-item v-for="task in completedTasks" :key="task.id" v-if="showCompleted">
                    <template v-slot:icon><i class="fas fa-check-circle fa-fw text-success"></i></template>
                    <template v-slot:content>
                        <div class="tile-title completed-title">{{task.title}}</div>
                        <div class="meta" v-if="task.assigned_first_name">{{task.assigned_first_name}} {{task.assigned_last_name}}</div>
                    </template>
                </card-list-item>

                <container-list-item padded v-if="loading" class="has-text-grey is-size-7">
                    <i class="fas fa-spinner fa-spin fa-fw mr-2 ml-2"></i>Loading...
                </container-list-item>

                <container-list-item padded v-if="!loading && tasks.length === 0" class="has-text-grey is-size-7">
                    No tasks found.
                </container-list-item>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardListItem from "@/TIER/components/CardListItem";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";

    import {client as http} from '@/http_client';

    export default {
        components: {ContainerListItem, CardHeader, CardBody, CardList, CardToolbar, CardListItem, SubheaderListItem},
        props: ['card', 'child', 'props'],
        data() {
            return {
                tasks: [],
                loading: false,
                filter: 'all',
                showCompleted: false
            }
        },
        computed: {
            pendingTasks() {
                return this.tasks.filter(t => t.status === 'pending');
            },
            inProgressTasks() {
                return this.tasks.filter(t => t.status === 'in_progress');
            },
            completedTasks() {
                return this.tasks.filter(t => t.status === 'completed');
            }
        },
        methods: {
            loadTasks(forced) {
                this.loading = true;
                let url = this.filter === 'my' ? 'support/tasks/my' : 'support/tasks';
                http.get(url, {force: forced}).then(response => {
                    this.tasks = response.data || [];
                    this.loading = false;
                }).catch(err => {
                    console.log(err);
                    this.loading = false;
                });
            },
            toggleFilter(f) {
                this.filter = f;
                this.loadTasks(true);
            },
            updateTaskStatus(task, status) {
                http.put('support/tasks/' + task.id, {status}).then(() => {
                    this.loadTasks(true);
                }).catch(err => console.log(err));
            },
            toggleTaskStatus(task) {
                if (task.status === 'pending') this.updateTaskStatus(task, 'in_progress');
                else if (task.status === 'in_progress') this.updateTaskStatus(task, 'completed');
            },
            reload() {
                this.loadTasks(true);
            }
        },
        mounted() {
            this.loadTasks();
        }
    }
</script>

<style lang="scss" scoped>
    .tile-title {
        font-size: 0.85rem;
        font-weight: 600;
    }
    .completed-title {
        text-decoration: line-through;
        color: #999;
    }
</style>
